import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  FaBookOpen,
  FaChevronDown,
  FaClock,
  FaFlask,
  FaGraduationCap,
  FaLightbulb,
  FaTools,
} from 'react-icons/fa';
import {
  caseStudies,
  courseMeta,
  keyMessage,
  learningContext,
  outcomes,
  prerequisites,
  programBlocks,
  schedule,
  softwareStack,
  timingRows,
} from './courseData';
import styles from './Teaching.module.css';

const navItems = [
  { id: 'objectives', label: 'Objectives' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'program', label: 'Program' },
  { id: 'cases', label: 'Research' },
  { id: 'setup', label: 'Setup' },
];

const Teaching: React.FC = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [openBlock, setOpenBlock] = useState<string | null>('01');

  const scrollTo = (id: string) => {
    document.querySelector(`#${id}`)?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleBlock = (id: string) => {
    setOpenBlock((prev) => (prev === id ? null : id));
  };

  return (
    <section id="teaching" className={styles.teaching} ref={ref}>
      <div className={styles.container}>
        <motion.div
          className={styles.pageHeader}
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          <h2 className={styles.pageTitle}>Teaching</h2>
          <div className={styles.pageUnderline} />
        </motion.div>

        <motion.article
          className={styles.courseHero}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.08 }}
        >
          <div className={styles.heroBand}>
            <FaGraduationCap />
            <span>{courseMeta.institution}</span>
          </div>

          <div className={styles.heroBody}>
            <p className={styles.moduleLabel}>{courseMeta.module}</p>
            <h3 className={styles.courseTitle}>
              AI for <em>Experimental Data</em> Analysis in Mechanical Systems Monitoring
            </h3>
            <p className={styles.courseLede}>{courseMeta.lede}</p>

            <div className={styles.heroMeta}>
              <div className={styles.metaChip}>
                <FaClock />
                <span>{courseMeta.duration}</span>
              </div>
              <div className={styles.metaChip}>
                <FaFlask />
                <span>4 labs</span>
              </div>
              <div className={styles.metaChip}>
                <FaBookOpen />
                <span>{courseMeta.language}</span>
              </div>
              <div className={styles.metaChip}>
                <span className={styles.metaChipLabel}>Programme</span>
                <span>{courseMeta.programme}</span>
              </div>
              <div className={styles.metaChip}>
                <span className={styles.metaChipLabel}>Instructor</span>
                <span>{courseMeta.instructor}</span>
              </div>
            </div>
          </div>
        </motion.article>

        <nav className={styles.subNav} aria-label="Course sections">
          {navItems.map((item) => (
            <button key={item.id} type="button" onClick={() => scrollTo(item.id)}>
              {item.label}
            </button>
          ))}
        </nav>

        <div className={styles.courseContent}>
          <section id="objectives" className={styles.panel}>
            <header className={styles.panelHead}>
              <span className={styles.panelLabel}>General objective</span>
              <h4>What students will be able to do</h4>
            </header>

            <div className={styles.callout}>
              <FaLightbulb />
              <p>{learningContext}</p>
            </div>

            <ul className={styles.outcomeGrid}>
              {outcomes.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>

          <section id="schedule" className={styles.panel}>
            <header className={styles.panelHead}>
              <span className={styles.panelLabel}>Course map</span>
              <h4>Five hours, end to end</h4>
            </header>

            <ul className={styles.scheduleList}>
              {schedule.map((row) => (
                <li key={row.id} className={row.isLab ? styles.labRow : undefined}>
                  <span className={styles.rowId}>{row.id}</span>
                  <span className={styles.rowTitle}>{row.title}</span>
                  <span className={styles.rowDuration}>{row.duration}</span>
                </li>
              ))}
            </ul>
          </section>

          <section id="program" className={styles.panel}>
            <header className={styles.panelHead}>
              <span className={styles.panelLabel}>Detailed program</span>
              <h4>Content, block by block</h4>
            </header>

            <div className={styles.accordion}>
              {programBlocks.map((block) => {
                const isOpen = openBlock === block.id;
                const isLab = block.type === 'lab';

                return (
                  <div
                    key={block.id}
                    className={`${styles.accordionItem} ${isLab ? styles.accordionLab : ''} ${
                      isOpen ? styles.accordionOpen : ''
                    }`}
                  >
                    <button
                      type="button"
                      className={styles.accordionTrigger}
                      onClick={() => toggleBlock(block.id)}
                      aria-expanded={isOpen}
                    >
                      <span className={styles.blockId}>{block.id}</span>
                      <span className={styles.blockTitle}>{block.title}</span>
                      <span className={styles.blockDuration}>{block.duration}</span>
                      <FaChevronDown className={styles.chevron} />
                    </button>

                    {isOpen && (
                      <div className={styles.accordionBody}>
                        {block.summary && <p className={styles.blockSummary}>{block.summary}</p>}

                        {block.topics?.map((topic) => (
                          <div key={topic.heading} className={styles.topicGroup}>
                            <h5>{topic.heading}</h5>
                            <ul>
                              {topic.items.map((item) => (
                                <li key={item}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        ))}

                        {block.items && (
                          <ul className={styles.simpleList}>
                            {block.items.map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        )}

                        {block.options && (
                          <ul className={styles.simpleList}>
                            {block.options.map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        )}

                        {block.tools && (
                          <p className={styles.toolsLine}>
                            <FaTools /> {block.tools}
                          </p>
                        )}

                        {block.discussion && (
                          <p className={styles.discussionLine}>{block.discussion}</p>
                        )}

                        {block.paper && <p className={styles.paperLine}>{block.paper}</p>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <div className={styles.keyBanner}>
            <span className={styles.panelLabel}>Key message</span>
            <p>{keyMessage}</p>
          </div>

          <section id="cases" className={styles.panel}>
            <header className={styles.panelHead}>
              <span className={styles.panelLabel}>Case studies</span>
              <h4>Every method, anchored to a real study</h4>
            </header>

            <p className={styles.panelIntro}>
              Labs use synthetic data to teach the method; each method is then illustrated by a
              published study from the instructor&apos;s research.
            </p>

            <div className={styles.caseGrid}>
              {caseStudies.map((study) => (
                <article key={study.id} className={styles.caseCard}>
                  <div className={styles.caseTop}>
                    <span className={styles.caseId}>{study.id}</span>
                    <span className={styles.caseMeta}>
                      {study.year} · {study.journal}
                    </span>
                  </div>
                  <h5>{study.title}</h5>
                  <p className={styles.caseAuthors}>{study.authors}</p>
                  <p className={styles.caseTags}>{study.tags}</p>
                  <p className={styles.caseUsed}>
                    <strong>Used in</strong> — {study.usedIn}
                  </p>
                </article>
              ))}
            </div>
          </section>

          <section id="setup" className={styles.panel}>
            <header className={styles.panelHead}>
              <span className={styles.panelLabel}>Practical setup</span>
              <h4>Tools &amp; prerequisites</h4>
            </header>

            <div className={styles.setupGrid}>
              <div className={styles.setupCard}>
                <h5>Software stack</h5>
                <ul>
                  {softwareStack.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className={styles.setupCard}>
                <h5>Expected background</h5>
                <ul>
                  {prerequisites.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className={styles.tableWrap}>
              <table className={styles.timingTable}>
                <thead>
                  <tr>
                    <th>Block</th>
                    <th>Content</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {timingRows.map((row) => (
                    <tr
                      key={row.block}
                      className={`${row.isLab ? styles.tableLab : ''} ${
                        row.isTotal ? styles.tableTotal : ''
                      }`}
                    >
                      <td>{row.block}</td>
                      <td>{row.content}</td>
                      <td>{row.duration}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </section>
  );
};

export default Teaching;